import { useEffect, useMemo, useRef, useState } from "react"
import { Box, HStack, Skeleton, Text } from "@chakra-ui/react"
import { FaCircleInfo, FaTriangleExclamation } from "react-icons/fa6"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { UnmapAssetDocument } from "@hydrologiq/gql-experience/graphql"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useMapAssetDataState, useMapAssetDataActions, useMapAssetDataStatus } from "../../providers/MapAssetDataProvider"
import {
  filterAssetsBySourceClass,
  sourceFitsAsset,
} from "@hydrologiq/shared/data-connections/sourceFilterUtils"
import { InlineMappingTable } from "./InlineMappingTable"
import { AddMappingRow } from "./AddMappingRow"
import {
  computeAvailableAssets,
  computeAvailableSources,
  isSourceMissing,
  formatStaleBanner,
} from "./mappingUtils"
import { COLOR_WARNING_BG } from "@hydrologiq/shared/data-connections/constants"
import { MappingLockTag } from "@hydrologiq/shared/data-connections/MappingDisplay"

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface ConnectionMappingPanelProps {
  isEditable: boolean
  /** The connection is managed on its asset, so nothing can be mapped from here. */
  managedOnAsset?: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ConnectionMappingPanel({ isEditable, managedOnAsset = false }: ConnectionMappingPanelProps) {
  const { sources, mappedAssets, systemAssets, loadingSources, loadingMapped } = useMapAssetDataState()
  const { refreshSources, mapAssetAndRefetch } = useMapAssetDataActions()
  const { refreshing, stale, discoveredAt, supportsRefresh } = useMapAssetDataStatus()

  const [unmapAssetMutation] = useMutationWithEndpoint(UnmapAssetDocument, undefined, "primary")

  // ─── Stale source toast (inactive connection only) ──────────────────────────

  const hasShownStaleToast = useRef(false)
  useEffect(() => {
    if (isEditable && stale && !hasShownStaleToast.current) {
      hasShownStaleToast.current = true
      toaster.warning({
        title: "Unable to reach the data source",
        description: "Sources shown may be outdated. Try reloading or check connection credentials.",
      })
    }
    // Reset when stale clears (successful refresh) so next stale triggers again
    if (!stale) {
      hasShownStaleToast.current = false
    }
  }, [isEditable, stale])

  // ─── Computed values ────────────────────────────────────────────────────────

  const staleCount = useMemo(
    () => mappedAssets.filter((m) => isSourceMissing(m.sourceRef, sources)).length,
    [mappedAssets, sources],
  )

  const availableAssets = useMemo(
    () => computeAvailableAssets(systemAssets, mappedAssets),
    [systemAssets, mappedAssets],
  )

  const availableSources = useMemo(
    () => computeAvailableSources(sources, mappedAssets),
    [sources, mappedAssets],
  )

  const unmappedSourceCount = availableSources.length
  const unmappedAssetCount = availableAssets.length

  // Compute compatible counts — only count assets/sources that have at least one compatible counterpart
  const compatibleAssetCount = useMemo(
    () => availableAssets.filter((asset) =>
      availableSources.some((s) => sourceFitsAsset(s, asset))
    ).length,
    [availableAssets, availableSources],
  )

  const compatibleSourceCount = useMemo(
    () => availableSources.filter((source) =>
      filterAssetsBySourceClass(availableAssets, source.compatibleClasses, source.fittingAssetIds).length > 0
    ).length,
    [availableAssets, availableSources],
  )

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleUpdateMapping = async (assetIntegrationId: string, field: "asset" | "source", value: string) => {
    try {
      const mapping = mappedAssets.find((m) => m.id === assetIntegrationId)
      if (!mapping) return

      // A change must never leave a source unmapped. The server can refuse the
      // new mapping (an AC Load with no scope, or a second Asset reading a
      // source that has no Connections), and removing the old mapping first
      // used to leave the Asset reading nothing, silently, until someone
      // noticed the gap in its data.
      if (field === "source") {
        // Same Asset, new source: the server checks the new mapping before it
        // replaces this Asset's old one on the connection, so a refusal leaves
        // the old mapping in place. No unmap needed.
        await mapAssetAndRefetch(mapping.assetId, value)
        return
      }

      // New Asset, same source: the old Asset has to let go first, because a
      // source with no Connections can only have one reader. If the new Asset
      // is refused, give the source back to the old one.
      await unmapAssetMutation({ variables: { assetIntegrationId } })
      try {
        await mapAssetAndRefetch(value, mapping.sourceRef)
      } catch (error) {
        await mapAssetAndRefetch(mapping.assetId, mapping.sourceRef).catch(() => undefined)
        throw error
      }
    } catch (error: any) {
      toaster.error({ title: "Failed to update mapping", description: error?.message ?? "An unexpected error occurred" })
    }
  }

  const handleDeleteMapping = async (assetIntegrationId: string) => {
    try {
      const { errors } = await unmapAssetMutation({ variables: { assetIntegrationId } })
      if (errors?.length) {
        throw new Error(errors[0].message)
      }
      // Refresh data to reflect the removed mapping
      await refreshSources()
    } catch (error: any) {
      toaster.error({ title: "Failed to remove mapping", description: error?.message ?? "An unexpected error occurred" })
    }
  }

  const handleAddMapping = async (assetId: string, sourceRef: string) => {
    try {
      await mapAssetAndRefetch(assetId, sourceRef)
    } catch (error: any) {
      toaster.error({ title: "Failed to map the source", description: error?.message ?? "An unexpected error occurred" })
    }
  }

  // ─── Reload cooldown (3 minutes after last successful discovery) ──────────────

  const RELOAD_COOLDOWN_MS = 3 * 60 * 1000
  const [cooldownRemaining, setCooldownRemaining] = useState(0)

  useEffect(() => {
    if (!discoveredAt) {
      setCooldownRemaining(0)
      return
    }

    const discoveredTime = new Date(discoveredAt).getTime()
    if (isNaN(discoveredTime)) {
      setCooldownRemaining(0)
      return
    }

    const tick = () => {
      const elapsed = Date.now() - discoveredTime
      const remaining = Math.max(0, RELOAD_COOLDOWN_MS - elapsed)
      setCooldownRemaining(remaining)
    }

    tick()
    const interval = setInterval(() => {
      const elapsed = Date.now() - discoveredTime
      const remaining = Math.max(0, RELOAD_COOLDOWN_MS - elapsed)
      setCooldownRemaining(remaining)
      if (remaining <= 0) clearInterval(interval)
    }, 10_000) // Update every 10s — no need for per-second precision
    return () => clearInterval(interval)
  }, [discoveredAt])

  const reloadOnCooldown = cooldownRemaining > 0

  const lastRefreshedLabel = useMemo(() => {
    if (!discoveredAt) return null
    const discoveredTime = new Date(discoveredAt).getTime()
    if (isNaN(discoveredTime)) return null
    const elapsedMs = Date.now() - discoveredTime
    if (elapsedMs < 0) return null
    const seconds = Math.floor(elapsedMs / 1000)
    if (seconds < 60) return "Last reloaded just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes >= 60) return "Last reloaded > 1h ago"
    return `Last reloaded ${minutes}m ago`
  }, [discoveredAt, cooldownRemaining]) // cooldownRemaining drives re-computation

  const handleReload = async () => {
    try {
      const isStale = await refreshSources()
      if (isStale) {
        toaster.warning({
          title: "Unable to reach the data source",
          description: "Sources shown may be outdated. Check connection credentials or try again shortly.",
        })
      } else {
        toaster.success({ title: "Sources reloaded successfully" })
      }
    } catch (error: any) {
      toaster.error({ title: "Failed to refresh sources", description: error?.message ?? "An unexpected error occurred" })
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Banners compare sources with mappings, so wait for both or they flash
  if (loadingSources || loadingMapped) return <Skeleton height="40px" width="100%" />

  return (
    <Box>
      {/* Heading row */}
      <HStack justifyContent={"space-between"} gap={3} mb={3} align="center">
        <HStack flexWrap="wrap">
          <Text fontWeight="semibold" fontSize="sm">Mapped assets</Text>

          <MappingLockTag isEditable={isEditable} />

        </HStack>

        {isEditable && supportsRefresh && (
          <HStack gap={2} align="center">
            {lastRefreshedLabel && (
              <Text fontSize="xs" color="fg.muted">{lastRefreshedLabel}</Text>
            )}
            <Box minW="120px">
              <ActionButton
                size="xs"
                disabled={refreshing || reloadOnCooldown}
                context={{
                  label: refreshing ? "Reloading…" : "Reload sources",
                  description: "Reload available sources from the connection",
                  variant: "secondary",
                }}
                onClick={handleReload}
              />
            </Box>
          </HStack>
        )}
      </HStack>

      {/* Mapping required banner — when sources exist but nothing is mapped */}
      {sources.length > 0 && mappedAssets.length === 0 && (
        <Box
          bg={COLOR_WARNING_BG}
          border="1px solid"
          borderColor="orange.300"
          borderRadius="md"
          px={3}
          py={2}
          mb={3}
        >
          <HStack gap={2} align="center">
            <Box color="orange.500">
              <FaTriangleExclamation />
            </Box>
            <Text fontSize="sm" color="orange.fg" fontWeight="medium">
              Device mapping required
            </Text>
          </HStack>
          <Text fontSize="sm" color="fg.muted" mt={1}>
            Map at least one data source to an asset to activate this connection.
          </Text>
        </Box>
      )}

      {/* Stale banner */}
      {staleCount > 0 && (
        <Box
          bg={COLOR_WARNING_BG}
          border="1px solid"
          borderColor="orange.300"
          borderRadius="md"
          px={3}
          py={2}
          mb={3}
        >
          <HStack gap={2} align="start">
            <Box color="orange.500" mt={0.5}>
              <FaTriangleExclamation />
            </Box>
            <Text fontSize="sm" color="primary">
              {formatStaleBanner(staleCount)}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Mapping table */}
      {mappedAssets.length > 0 && (
        <InlineMappingTable
          mappedAssets={mappedAssets}
          sources={sources}
          systemAssets={systemAssets}
          isEditable={isEditable}
          onUpdateMapping={handleUpdateMapping}
          onDeleteMapping={handleDeleteMapping}
        />
      )}

      {/* Add mapping row (editable only) */}
      {isEditable && (
        <Box mt={3}>
          <AddMappingRow
            availableAssets={availableAssets}
            availableSources={availableSources}
            onAdd={handleAddMapping}
          />
        </Box>
      )}

      {/* Locked additional sources banner (active connection) */}
      {!isEditable && !managedOnAsset && (compatibleSourceCount > 0 && compatibleAssetCount > 0) && (
        <Box
          bg="#f0fafe"
          borderRadius="md"
          border="1px solid"
          borderColor="#d4eef7"
          px={4}
          py={3}
          mt={3}
        >
          <HStack gap={2} align="center">
            <Box color="primary" display="inline-flex">
              <FaCircleInfo style={{ fontSize: "13px" }} />
            </Box>
            <Text fontSize="sm" color="primary">
              {compatibleAssetCount} asset{compatibleAssetCount === 1 ? "" : "s"} available to map to {compatibleSourceCount} source{compatibleSourceCount === 1 ? "" : "s"}. Deactivate the connection to map them.
            </Text>
          </HStack>
        </Box>
      )}
    </Box>
  )
}

export default ConnectionMappingPanel
